<!-- Derived from Moriafly/SaltUI Slider.kt (layout and SliderDefaults) and
     Item.kt/ItemSlider at 2b2bb13df656644f554771f44fd0dcee0e296aee.
     Copyright 2023-2026 Moriafly. Apache-2.0, see LICENSE.
     Bare slider: its parent owns ItemSlider's label and 16/12dp spacing.
     Native range owns pointer, touch, keyboard and accessible value semantics. -->
<template>
  <span class="salt-ui-slider" :class="{ disabled }">
    <span class="slider-rail" aria-hidden="true"><span class="slider-fill" :style="fillStyle" /></span>
    <input class="slider-input" type="range" :min="rangeMin" :max="rangeMax" :step="rangeStep" :value="currentValue" :disabled="disabled" v-bind="nativeAttrs" v-on="nativeListeners" @input="onInput" @change="onChange" />
  </span>
</template>
<script>
export default {
  name: 'SaltSlider', inheritAttrs: false,
  props: { value: { type: Number, default: 0 }, min: { type: Number, default: 0 }, max: { type: Number, default: 1 }, step: { type: Number, default: .01 }, disabled: Boolean },
  data() { return { draftValue: this.value }; },
  computed: {
    rangeMin() { return Number.isFinite(this.min) ? this.min : 0; },
    rangeMax() { return Math.max(this.rangeMin, Number.isFinite(this.max) ? this.max : 1); },
    rangeStep() { return Number.isFinite(this.step) && this.step > 0 ? this.step : .01; },
    currentValue() { return this.normalizeValue(this.draftValue); },
    fillStyle() {
      const range = this.rangeMax - this.rangeMin;
      const fraction = range > 0 ? (this.currentValue - this.rangeMin) / range : 0;
      // Track canvas width = total width - thumb24. Its round caps extend 11.5px.
      return { width: `calc(${fraction * 100}% + 23px)` };
    },
    nativeAttrs() { const attrs = { ...this.$attrs }; for (const key of ['type', 'value', 'min', 'max', 'step', 'disabled']) delete attrs[key]; return attrs; },
    nativeListeners() { const listeners = { ...this.$listeners }; for (const key of ['input', 'change', 'change-finished']) delete listeners[key]; return listeners; },
  },
  watch: { value(value) { this.draftValue = value; } },
  methods: {
    normalizeValue(value) {
      const bounded = Math.max(this.rangeMin, Math.min(this.rangeMax, Number.isFinite(value) ? value : this.rangeMin));
      const lastStep = Math.floor((this.rangeMax - this.rangeMin) / this.rangeStep + 1e-10);
      const steps = Math.min(lastStep, Math.round((bounded - this.rangeMin) / this.rangeStep));
      return Math.max(this.rangeMin, Math.min(this.rangeMax, Number((this.rangeMin + steps * this.rangeStep).toPrecision(15))));
    },
    onInput(event) {
      if (this.disabled) return;
      const value = this.normalizeValue(event.target.valueAsNumber);
      this.draftValue = value;
      this.$emit('input', value);
      this.$emit('change', value);
    },
    onChange(event) {
      if (this.disabled) return;
      const value = this.normalizeValue(event.target.valueAsNumber);
      if (value !== this.currentValue) this.onInput(event);
      this.$emit('change-finished', value);
    },
  },
};
</script>
<style scoped>
.salt-ui-slider { position:relative; display:inline-flex; flex:1 1 auto; width:100%; min-width:24px; height:24px; color:var(--salt-text,#1e1715); vertical-align:middle; }
.slider-rail { position:absolute; inset-block-start:.5px; inset-inline:12px; height:23px; pointer-events:none; }
.slider-rail::before { content:''; position:absolute; inset:0 -11.5px; border-radius:9999px; background:rgb(140 140 140 / 10%); background:rgb(from var(--salt-subtext,#8c8c8c) r g b / 10%); }
.slider-fill { position:absolute; inset-block-start:0; inset-inline-start:-11.5px; height:23px; border-radius:9999px; background:var(--salt-accent,#0470e6); }
.slider-input { position:relative; display:block; box-sizing:border-box; width:100%; min-width:0; height:24px; padding:0; margin:0; border:0; border-radius:9999px; outline:none; background:transparent; color:inherit; appearance:none; -webkit-appearance:none; cursor:default; box-shadow:none; transform:none; }
.slider-input::-webkit-slider-runnable-track { box-sizing:border-box; height:24px; border:0; background:transparent; box-shadow:none; }
.slider-input::-webkit-slider-thumb { box-sizing:border-box; width:24px; height:24px; margin:0; border:1px solid rgb(140 140 140 / 25%); border-color:rgb(from var(--salt-subtext,#8c8c8c) r g b / 25%); border-radius:50%; background:var(--salt-on-accent,#fff); box-shadow:none; appearance:none; -webkit-appearance:none; }
.slider-input::-moz-range-track { box-sizing:border-box; height:24px; border:0; background:transparent; box-shadow:none; }
.slider-input::-moz-range-progress { background:transparent; }
.slider-input::-moz-range-thumb { box-sizing:border-box; width:24px; height:24px; border:1px solid rgb(140 140 140 / 25%); border-color:rgb(from var(--salt-subtext,#8c8c8c) r g b / 25%); border-radius:50%; background:var(--salt-on-accent,#fff); box-shadow:none; }
.slider-input:focus-visible { outline:2px solid var(--salt-accent,#0470e6); outline-offset:2px; }
.slider-input:disabled { opacity:1; cursor:default; }
@media(prefers-reduced-motion:reduce) { .slider-input,.slider-rail,.slider-fill { animation:none; transition:none; } }
</style>
